'use client'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getTokenImage } from '@/ui/assets/tokens'
import { Button } from '@/ui/atoms/button/Button'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowRight, Check, Plus } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'

export interface ConfigSummary {
  mode: string
  market: string
  positionLabel?: string
  borrowToken?: string
  borrowAPY?: number
  spendingPower?: number
  ltv?: number
  collateralValue?: number
}

interface ActivateConfirmationModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<boolean>
  isLoading: boolean
  from: ConfigSummary | null
  to: ConfigSummary
  isNewPosition?: boolean
  newPositionSubAccountId?: number
  needsDelegation?: boolean
  eulerCluster?: 'prime' | 'yield'
  onDepositCollateral?: () => void
}

export function ActivateConfirmationModal({
  open,
  onClose,
  onConfirm,
  isLoading,
  from,
  to,
  isNewPosition,
  newPositionSubAccountId,
  needsDelegation,
  onDepositCollateral,
}: ActivateConfirmationModalProps) {
  const [step, setStep] = useState<'confirm' | 'success'>('confirm')

  const apyDiff = from?.borrowAPY != null && to.borrowAPY != null ? to.borrowAPY - from.borrowAPY : null
  const apyIncreased = apyDiff != null && apyDiff > 0.01
  const apyDecreased = apyDiff != null && apyDiff < -0.01

  const spendingPowerDiff =
    from?.spendingPower != null && to.spendingPower != null ? to.spendingPower - from.spendingPower : null
  const ltvDiff = from?.ltv != null && to.ltv != null ? to.ltv - from.ltv : null

  const handleClose = () => {
    setStep('confirm')
    onClose()
  }

  const handleConfirm = async () => {
    const ok = await onConfirm()
    if (!ok) return
    if (isNewPosition) {
      setStep('success')
    } else {
      handleClose()
    }
  }

  const handleGoToDeposit = () => {
    setStep('confirm')
    onClose()
    onDepositCollateral?.()
  }

  if (step === 'success' && isNewPosition) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogTitle>
            <Typography variant="span" className="font-semibold text-base">
              Position Activated
            </Typography>
          </DialogTitle>
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <Check className="h-6 w-6 text-green-400" />
            </div>
            <Typography variant="h4" className="text-center font-semibold">
              Position activated successfully
            </Typography>
            <Typography variant="p" className="text-center text-muted-foreground text-sm">
              Position {newPositionSubAccountId} is ready to use.
            </Typography>
            <Typography variant="p" className="text-center text-blue-300 text-xs">
              Deposit collateral into this position to enable spending power.
            </Typography>
            <div className="flex gap-3">
              <Button
                onClick={handleGoToDeposit}
                className="bg-gradient-to-r from-gradient-yellow to-gradient-green text-black"
              >
                <Plus className="mr-2 h-4 w-4" />
                Deposit Collateral
              </Button>
              <Button variant="outline" onClick={handleClose}>
                I&apos;ll do this later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // When switching to spend mode, always show spending power even without a diff
  const isModeSwitchToSpend = to.mode === 'Spend' && from?.mode !== 'Spend'
  const showSpendingPower = spendingPowerDiff != null || (isModeSwitchToSpend && to.spendingPower != null)
  const hasImpactData =
    showSpendingPower || (apyDiff != null && to.mode !== 'Spend') || (ltvDiff != null && to.mode !== 'Spend')

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-xl">
        <DialogTitle>
          <Typography variant="span" className="font-semibold text-base">
            Confirm Configuration Change
          </Typography>
        </DialogTitle>
        <div className="space-y-5 pt-1">
          {from && (
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-5">
              <ConfigCard label="Current" config={from} variant="from" />
              <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              <ConfigCard label="New" config={to} variant="to" />
            </div>
          )}
          {!from && <ConfigCard label="Activating" config={to} variant="to" />}

          {hasImpactData && (
            <div className="space-y-2 rounded-lg border border-border/50 bg-muted/10 p-4">
              <Typography
                variant="p"
                className="mb-3 font-medium text-muted-foreground text-xs uppercase tracking-wider"
              >
                Impact
              </Typography>

              {showSpendingPower && to.spendingPower != null && (
                <div className="flex items-center justify-between py-1.5">
                  <Typography variant="p" className="text-sm">
                    Spending Power
                  </Typography>
                  <div className="flex items-center gap-2">
                    {from?.spendingPower != null && (
                      <>
                        <Typography variant="p" className="text-muted-foreground text-sm">
                          ${from.spendingPower.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </Typography>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      </>
                    )}
                    <Typography variant="p" className="font-medium text-sm">
                      $
                      {to.spendingPower.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                    {spendingPowerDiff != null && Math.abs(spendingPowerDiff) > 0.01 && (
                      <span
                        className={cn(
                          'ml-1 rounded-full px-2 py-0.5 font-medium text-xs',
                          spendingPowerDiff > 0 ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400',
                        )}
                      >
                        {spendingPowerDiff > 0 ? '+' : '-'}$
                        {Math.abs(spendingPowerDiff).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {apyDiff != null && Math.abs(apyDiff) > 0.001 && to.mode !== 'Spend' && (
                <div className="flex items-center justify-between py-1.5">
                  <Typography variant="p" className="text-sm">
                    Borrow Rate
                  </Typography>
                  <div className="flex items-center gap-2">
                    {from?.borrowAPY != null && (
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        {from.borrowAPY.toFixed(2)}%
                      </Typography>
                    )}
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <Typography variant="p" className="font-medium text-sm">
                      {to.borrowAPY!.toFixed(2)}%
                    </Typography>
                    <span
                      className={cn(
                        'ml-1 rounded-full px-2 py-0.5 font-medium text-xs',
                        apyDecreased
                          ? 'bg-green-500/15 text-green-400'
                          : apyIncreased
                            ? 'bg-red-500/15 text-red-400'
                            : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {apyDiff > 0 ? '+' : ''}
                      {apyDiff.toFixed(2)}%
                    </span>
                  </div>
                </div>
              )}

              {ltvDiff != null && Math.abs(ltvDiff) > 0.001 && to.mode !== 'Spend' && (
                <div className="flex items-center justify-between py-1.5">
                  <Typography variant="p" className="text-sm">
                    Max LTV
                  </Typography>
                  <div className="flex items-center gap-2">
                    {from?.ltv != null && (
                      <Typography variant="p" className="text-muted-foreground text-sm">
                        {(from.ltv * 100).toFixed(0)}%
                      </Typography>
                    )}
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <Typography variant="p" className="font-medium text-sm">
                      {(to.ltv! * 100).toFixed(0)}%
                    </Typography>
                    <span
                      className={cn(
                        'ml-1 rounded-full px-2 py-0.5 font-medium text-xs',
                        ltvDiff > 0
                          ? 'bg-green-500/15 text-green-400'
                          : ltvDiff < 0
                            ? 'bg-red-500/15 text-red-400'
                            : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {ltvDiff > 0 ? '+' : ''}
                      {(ltvDiff * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {isNewPosition && (
            <div className="space-y-2 rounded-lg border border-border/50 bg-muted/10 p-4">
              <Typography variant="p" className="mb-2 font-medium text-sm">
                This will:
              </Typography>
              <ul className="space-y-2 text-muted-foreground text-sm">
                {to.mode !== 'Spend' && (
                  <li className="flex items-center gap-2.5">
                    <Check className="h-3.5 w-3.5 text-green-400" />
                    Enable debt controller for Position {newPositionSubAccountId}
                  </li>
                )}
                {needsDelegation && (
                  <li className="flex items-center gap-2.5">
                    <Check className="h-3.5 w-3.5 text-green-400" />
                    Authorize card operator
                  </li>
                )}
                <li className="flex items-center gap-2.5">
                  <Check className="h-3.5 w-3.5 text-green-400" />
                  Activate card configuration
                </li>
              </ul>
            </div>
          )}

          {!isNewPosition && needsDelegation && (
            <div className="rounded-lg border border-border/50 bg-muted/10 p-4">
              <Typography variant="p" className="text-muted-foreground text-sm">
                {to.mode === 'Spend'
                  ? 'This will authorize the card operator to withdraw from your vault when you spend.'
                  : 'This will also approve delegation for the card operator.'}
              </Typography>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-gradient-yellow to-gradient-green text-black"
            >
              {isLoading ? 'Confirming...' : 'Confirm'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ConfigCard({ label, config, variant }: { label: string; config: ConfigSummary; variant: 'from' | 'to' }) {
  return (
    <div
      className={cn(
        'flex-1 rounded-lg border p-3',
        variant === 'from' ? 'border-border/50 bg-muted/20' : 'border-primary/30 bg-primary/5',
      )}
    >
      <Typography variant="p" className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </Typography>
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Typography variant="p" className="font-medium text-sm">
            {config.mode}
          </Typography>
          <span className="text-muted-foreground text-xs">&middot;</span>
          <Typography variant="p" className="text-sm">
            {config.market}
          </Typography>
        </div>

        {config.positionLabel && (
          <Typography variant="p" className="text-muted-foreground text-xs">
            {config.positionLabel}
          </Typography>
        )}

        {config.borrowToken && (
          <div className="flex items-center gap-1.5">
            <Image
              src={getTokenImage(TokenSymbol(config.borrowToken))}
              alt={config.borrowToken}
              width={14}
              height={14}
              className="rounded-full"
            />
            <Typography variant="p" className="text-xs">
              {config.borrowToken}
            </Typography>
          </div>
        )}

        {config.borrowAPY != null && config.mode !== 'Spend' && (
          <Typography variant="p" className="text-muted-foreground text-xs">
            {config.borrowAPY.toFixed(2)}% APY
          </Typography>
        )}
      </div>
    </div>
  )
}
